import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'

const UserProfile = () => {
  const { user, signOut, updateProfile } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [name, setName] = useState('')
  const [trackingMode, setTrackingMode] = useState('cycle')
  const [lastPeriodDate, setLastPeriodDate] = useState('')
  const [cycleLength, setCycleLength] = useState(28)
  const [success, setSuccess] = useState('')

  useEffect(() => {
    if (user) {
      setName(user.user_metadata?.full_name || '')
      
      // Load from localStorage as backup
      const storedMode = localStorage.getItem('trackingMode')
      const storedDate = localStorage.getItem('lastPeriodDate')
      const storedLength = localStorage.getItem('cycleLength')
      
      if (storedMode) setTrackingMode(storedMode)
      if (storedDate) setLastPeriodDate(storedDate)
      if (storedLength) setCycleLength(parseInt(storedLength))
    }
  }, [user])

  const handleSaveProfile = async () => {
    setLoading(true)
    setSuccess('')
    
    try {
      // Update auth user metadata
      const { error } = await updateProfile({
        full_name: name,
        tracking_mode: trackingMode
      })

      if (error) throw error

      // Save to localStorage as backup
      localStorage.setItem('trackingMode', trackingMode)
      if (trackingMode === 'cycle') {
        localStorage.setItem('lastPeriodDate', lastPeriodDate)
        localStorage.setItem('cycleLength', cycleLength.toString())
      } else {
        // Clear cycle data if switching to moon mode
        localStorage.removeItem('lastPeriodDate')
        localStorage.removeItem('cycleLength')
      }

      setSuccess('Profile saved successfully!')
      setTimeout(() => navigate('/'), 1500)
    } catch (error) {
      console.error('Error saving profile:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/auth')
  }

  return (
    <div className="min-h-screen bg-white px-6 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-md mx-auto"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-500 mb-4 hover:text-black transition-colors"
          >
            ← Back to Home
          </button>
          <h1 className="italiana-font text-2xl text-black mb-2">
            Your Profile
          </h1>
          <p className="text-sm text-gray-600">
            Manage your account and cycle preferences
          </p>
        </div>

        {/* Account Info */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h3 className="font-medium text-black mb-2">Account</h3>
          <p className="text-sm text-gray-600">{user?.email}</p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-green-600">{success}</p>
          </div>
        )}

        {/* Profile Form */}
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Your Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
              placeholder="Enter your name"
            />
          </div>

          {/* Tracking Mode */}
          <div>
            <h3 className="text-sm font-medium text-black mb-4">
              Tracking Preference
            </h3>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setTrackingMode('cycle')}
                className={`w-full p-4 text-left border rounded-lg transition-all ${
                  trackingMode === 'cycle' 
                    ? 'border-gold bg-gold bg-opacity-10' 
                    : 'border-gray-300'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className={`w-4 h-4 rounded-full border-2 mt-1 ${
                    trackingMode === 'cycle' 
                      ? 'border-gold bg-gold' 
                      : 'border-gray-300'
                  }`} />
                  <div>
                    <h4 className="font-medium text-black">🩸 Menstrual Cycle + Moon</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Track your period for precise guidance
                    </p>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setTrackingMode('moon')}
                className={`w-full p-4 text-left border rounded-lg transition-all ${
                  trackingMode === 'moon' 
                    ? 'border-gold bg-gold bg-opacity-10' 
                    : 'border-gray-300'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className={`w-4 h-4 rounded-full border-2 mt-1 ${
                    trackingMode === 'moon' 
                      ? 'border-gold bg-gold' 
                      : 'border-gray-300'
                  }`} />
                  <div>
                    <h4 className="font-medium text-black">🌙 Moon Phases Only</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Follow lunar cycles for guidance
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Cycle Details */}
          {trackingMode === 'cycle' && (
            <>
              <div>
                <label className="block text-sm font-medium text-black mb-3">
                  Last Period Start Date
                </label>
                <input
                  type="date"
                  value={lastPeriodDate}
                  onChange={(e) => setLastPeriodDate(e.target.value)}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-3">
                  Cycle Length
                </label>
                <select
                  value={cycleLength}
                  onChange={(e) => setCycleLength(parseInt(e.target.value))}
                  className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
                >
                  {[21,22,23,24,25,26,27,28,29,30,31,32,33,34,35].map(length => (
                    <option key={length} value={length}>
                      {length} days
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Save Button */}
          <motion.button
            onClick={handleSaveProfile}
            disabled={loading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full gold-bg text-white py-4 px-6 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Profile'}
          </motion.button>

          {/* Sign Out */}
          <button
            onClick={handleSignOut}
            className="w-full text-gray-500 py-2 text-sm hover:text-black transition-colors"
          >
            Sign Out
          </button>
        </div>
      </motion.div>
    </div>
  )
}

export default UserProfile